import ThemeExtractor from "../core/components/ThemeExtractor";
import RichTextRenderer from "../forms/components/RichText/RichTextRenderer";
import { lgMarginBottomStyles } from "../neutral/styles/spacing";
import Photo from "../pages/website/Photo";
import { RichTextType } from "../resources/modelTypes";
import {
  imageAlignmentMap,
  imageSizeMap,
  imageSpacingMap,
  textAlignmentMap,
} from "./configuration";
import { bottomSpacerOnly, flex, flex1, m20 } from "./styles";
import { imageAlignmentAllowsFixedWidth } from "./util";
import { css, SerializedStyles } from "@emotion/react";
import {
  MediumHeadline,
  SmallHeadline,
  TinyHeadline,
} from "@minted/fancyclothes";
import { theme } from "@minted/minted-components";
import React from "react";

const contentWrapperStyles = {
  noVerticalMargin: css`
    margin-bottom: 0 !important;
    margin-top: 0 !important;
  `,
};

/*A fixed width applies only to screen sizes larger than medium-size breakpoints,
but only alignment allows it as well.
*/
const smallHeadlineStyles = (allowFixedWidth: boolean) => css`
  ${theme.media.greaterThan(theme.breakpointNames.medium)(css`
    width: ${allowFixedWidth ? "935px" : "auto"};
  `)}

  ${bottomSpacerOnly}
`;

export type ContentTextProps = {
  children: React.ReactNode;
  extraCss?: SerializedStyles[];
  textAlignment: string;
};

export const ContentText = ({
  children,
  extraCss = [],
  textAlignment,
}: ContentTextProps) => (
  <div css={[flex1, textAlignmentMap.get(textAlignment), ...extraCss]}>
    {children}
  </div>
);

export type ContentWrapperProps = {
  children: React.ReactNode;
  extraCss?: SerializedStyles[];
  imageAlignment: string;
  noVerticalMargin?: boolean;
  textAlignment: string;
};

export const ContentWrapper = ({
  children,
  extraCss = [],
  imageAlignment,
  noVerticalMargin = false,
  textAlignment,
}: ContentWrapperProps) => (
  <div
    css={[
      m20,
      ...(noVerticalMargin
        ? [contentWrapperStyles.noVerticalMargin]
        : [lgMarginBottomStyles]),
      flex,
      imageAlignmentMap.get(imageAlignment),
      textAlignmentMap.get(textAlignment),
      ...extraCss,
    ]}
  >
    {children}
  </div>
);

export type HeadlineMediumProps = {
  children: React.ReactNode;
};

export const HeadlineMedium = ({ children }: HeadlineMediumProps) => (
  <div css={[bottomSpacerOnly]}>
    <MediumHeadline>{children}</MediumHeadline>
  </div>
);

export type HeadlineSmallProps = {
  children: React.ReactNode;
  imageAlignment?: string;
};

export const HeadlineSmall = ({
  children,
  imageAlignment = "",
}: HeadlineSmallProps) => {
  const allowFixWidth = imageAlignmentAllowsFixedWidth(imageAlignment); //To determine if the image-text alignment allows us to have fixed width in SmallHeadline

  return (
    <div css={smallHeadlineStyles(allowFixWidth)}>
      <SmallHeadline>{children}</SmallHeadline>
    </div>
  );
};

export type HeadlineTinyProps = {
  children: React.ReactNode;
};

export const HeadlineTiny = ({ children }: HeadlineTinyProps) => (
  <div css={[bottomSpacerOnly]}>
    <TinyHeadline>{children}</TinyHeadline>
  </div>
);

export type ImageProps = {
  image: string | null | undefined;
  imageAlignment: string;
  imageSize: number;
  textAlignment?: string | null;
};

export const Image = ({
  image,
  imageAlignment,
  imageSize,
  textAlignment = null,
}: ImageProps) =>
  image ? (
    <div
      css={[
        imageSpacingMap.get(imageAlignment),
        imageSizeMap(imageSize),
        ...(textAlignment ? [textAlignmentMap.get(textAlignment)] : []),
      ]}
    >
      <Photo url={image} />
    </div>
  ) : null;

export type RichTextContentProps = {
  skipAnchorStyles?: boolean;
  value: RichTextType | null | undefined;
};

export const RichTextContent = ({
  skipAnchorStyles = false,
  value,
}: RichTextContentProps) =>
  value ? (
    <ThemeExtractor rules={["linkColor"]}>
      {({ linkColor }) => (
        <div
          css={css`
            a {
              color: ${skipAnchorStyles ? "unset" : linkColor};
            }
          `}
        >
          <RichTextRenderer value={value} />
        </div>
      )}
    </ThemeExtractor>
  ) : null;
